import * as React from 'react';
import { useToast } from '@cian/ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import { getComparisonStatus, getNewbuildingId } from '../../selectors/newbuilding';
import { getGALabel } from '../../selectors/newbuilding/getGALabel';
import { getIsAuthenticated } from '../../selectors/user';
import { requestAuthentication } from '../../actions/authentication';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from '../../services/propertyComparison';
import { setComparisonStatus } from '../../slices/newbuildingReducer';
import { TDispatch } from '../../types/redux';
import { useApplicationContext } from '../../utils/applicationContext';
import { CompareButtonControl } from './CompareButtonControl';
import { CompareButtonOnboarding } from './CompareButtonOnboarding';
import { CompareToastContent } from './CompareToastContent';
import { COMPARE_ENABLED_CONFIG } from './constants';
import {
  trackAddNewbuildingToComparison,
  trackClickAddComparisonPopup,
  trackClickRemoveComparisonPopup,
  trackRemoveNewbuildingFromComparison,
} from './tracking';
import { useCompareOnboarding } from './useCompareOnboarding';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingCompareButtonProps {
  hideText?: boolean;
  isHeader?: boolean;
  withOnboarding?: boolean;
}

export const NewbuildingCompareButton: React.FC<INewbuildingCompareButtonProps> = ({
  hideText = false,
  isHeader = false,
  withOnboarding = false,
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch<TDispatch>();
  const comparisonStatus = useSelector(getComparisonStatus);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const newbuildingId = useSelector(getNewbuildingId);
  const gaLabel = useSelector(getGALabel);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;
  const isAdded = comparisonStatus === 'added';
  const isComparisonAvailable = comparisonStatus === 'added' || comparisonStatus === 'available';
  const { open, closeHandler } = useCompareOnboarding(withOnboarding && isCompareEnabled && isComparisonAvailable);

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult) => {
      toast.open({
        type: result.success === false ? 'error' : 'success',
        content: (
          <CompareToastContent
            text={result.text}
            comparisonUrl={result.comparisonUrl}
            onClick={() => trackClickAddComparisonPopup(newbuildingId)}
          />
        ),
        onClose: () => trackClickRemoveComparisonPopup(newbuildingId),
      });
    },
    [toast, newbuildingId],
  );

  const handleClick = React.useCallback(async () => {
    const previousStatus = comparisonStatus;
    const nextStatus = isAdded ? 'available' : 'added';

    if (!isAuthenticated) {
      dispatch(requestAuthentication({}));

      return;
    }

    dispatch(setComparisonStatus(nextStatus));

    try {
      const result = !isAdded
        ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId })
        : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId });

      showComparisonToast(result);

      if (result.success === false) {
        dispatch(setComparisonStatus(previousStatus));

        return;
      }

      if (!isAdded) {
        trackAddNewbuildingToComparison(newbuildingId, gaLabel);
      } else {
        trackRemoveNewbuildingFromComparison(newbuildingId, gaLabel);
      }
    } catch (_error) {
      dispatch(setComparisonStatus(previousStatus));
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [
    comparisonStatus,
    dispatch,
    gaLabel,
    httpApi,
    isAdded,
    isAuthenticated,
    logger,
    newbuildingId,
    showComparisonToast,
  ]);

  if (!isCompareEnabled || !isComparisonAvailable) {
    return null;
  }

  const button = (
    <CompareButtonControl hideText={hideText} isHeader={isHeader} isAdded={isAdded} onClick={handleClick} />
  );

  if (!withOnboarding || !open) {
    return button;
  }

  return (
    <CompareButtonOnboarding open={open} onClose={closeHandler}>
      {button}
    </CompareButtonOnboarding>
  );
};
